import { useState, useEffect } from 'react';
import api from '../api';
import { T } from '../styles/theme';

export default function EbayConnect({ onStatusChange, compact = false }) {
  const [ebayStatus, setEbayStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [manualSaving, setManualSaving] = useState(false);

  const fetchStatus = () => {
    api.get('/auth/ebay-status').then((r) => {
      setEbayStatus(r.data);
      if (onStatusChange) onStatusChange(r.data);
    }).catch(() => setEbayStatus({ collegato: false }));
  };

  useEffect(() => { fetchStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'ebay_oauth') {
        setConnecting(false);
        setShowManual(false);
        if (e.data.ebay === 'success') {
          fetchStatus();
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async () => {
    try {
      const r = await api.get('/auth/ebay-link');
      setConnecting(true);
      const w = 600, h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(r.data.url, 'ebay_auth', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
      setTimeout(() => {
        if (!popup || popup.closed) {
          setConnecting(false);
          setShowManual(true);
        }
      }, 3000);
      setTimeout(() => setShowManual(true), 15000);
    } catch {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.delete('/auth/ebay-disconnect');
      setEbayStatus({ collegato: false });
      if (onStatusChange) onStatusChange({ collegato: false });
    } catch {}
  };

  const handleManualCode = async () => {
    if (!manualCode.trim()) return;
    setManualSaving(true);
    try {
      await api.post('/auth/ebay-exchange', { code: manualCode.trim() });
      setShowManual(false);
      setManualCode('');
      setConnecting(false);
      fetchStatus();
    } catch {
    } finally { setManualSaving(false); }
  };

  if (!ebayStatus) return null;

  // Versione compatta (banner inline)
  if (compact) {
    if (ebayStatus.collegato) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 12, background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)' }}>
          <span style={{ color: T.green, fontSize: '0.8rem', fontWeight: 600 }}>eBay collegato</span>
          {ebayStatus.username && <span style={{ color: T.muted, fontSize: '0.75rem' }}>@{ebayStatus.username}</span>}
        </div>
      );
    }
    return (
      <div style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ color: T.amber, fontSize: '0.8rem', fontWeight: 600 }}>Collega il tuo account eBay per pubblicare</span>
          <button onClick={handleConnect} disabled={connecting} style={{
            padding: '5px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
            background: T.gradPrimary, color: '#fff', whiteSpace: 'nowrap',
          }}>
            {connecting ? 'In corso...' : 'Collega eBay'}
          </button>
        </div>
        {showManual && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <input type="text" value={manualCode} onChange={(e) => setManualCode(e.target.value)}
              placeholder="Incolla il codice eBay..." style={{
                flex: 1, padding: '6px 10px', borderRadius: 8, border: `1px solid ${T.border}`,
                background: T.elem, color: T.text, fontSize: '0.75rem', outline: 'none',
              }} />
            <button onClick={handleManualCode} disabled={manualSaving || !manualCode.trim()} style={{
              padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: T.violetDark, color: '#fff', fontSize: '0.75rem', fontWeight: 600,
            }}>
              {manualSaving ? '...' : 'Collega'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Versione completa (card)
  return (
    <div style={{ padding: 16, borderRadius: 16, background: T.elem, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: T.violet }}>eBay</span>
          <span style={{
            padding: '3px 10px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 600,
            background: ebayStatus.collegato ? 'rgba(74,222,128,0.15)' : 'rgba(107,114,128,0.2)',
            color: ebayStatus.collegato ? T.green : T.muted,
          }}>
            {ebayStatus.collegato ? 'Collegato' : 'Non collegato'}
          </span>
          {ebayStatus.collegato && ebayStatus.username && (
            <span style={{ color: T.faint, fontSize: '0.8rem' }}>@{ebayStatus.username}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {ebayStatus.collegato && (
            <button onClick={handleDisconnect} style={{
              padding: '5px 12px', borderRadius: 8, border: `1px solid ${T.border}`, cursor: 'pointer',
              background: 'transparent', color: T.muted, fontSize: '0.75rem',
            }}>
              Scollega
            </button>
          )}
          <button onClick={handleConnect} disabled={connecting} style={{
            padding: '6px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
            background: ebayStatus.collegato ? 'transparent' : T.gradPrimary,
            color: ebayStatus.collegato ? T.violet : '#fff',
            border: ebayStatus.collegato ? `1px solid ${T.border}` : 'none',
          }}>
            {connecting ? 'In corso...' : ebayStatus.collegato ? 'Rinnova' : 'Collega eBay'}
          </button>
        </div>
      </div>
      {showManual && !ebayStatus.collegato && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: `${T.card}80`, border: `1px solid ${T.border}` }}>
          <p style={{ color: T.text, fontSize: '0.8rem', marginBottom: 8 }}>
            Se il redirect non funziona, copia il <strong style={{ color: '#fff' }}>codice</strong> dall'URL eBay (dopo <code style={{ color: T.violet }}>code=</code>) e incollalo qui:
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" value={manualCode} onChange={(e) => setManualCode(e.target.value)}
              placeholder="Incolla il codice eBay..." style={{
                flex: 1, padding: '8px 12px', borderRadius: 10, border: `1px solid ${T.border}`,
                background: T.elem, color: T.text, fontSize: '0.8rem', outline: 'none',
              }} />
            <button onClick={handleManualCode} disabled={manualSaving || !manualCode.trim()} style={{
              padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: T.violetDark, color: '#fff', fontSize: '0.8rem', fontWeight: 600,
            }}>
              {manualSaving ? '...' : 'Collega'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
